import React, { useEffect } from 'react';
import { submitFormData } from '../../../../pages/api';

const CellAndCuttingMachine = ({ formData, onFormDataChange }) => {
    const { cellData = [], cellCuttingData = [], observations = { cell: [], cellCutting: [] }, nonConformance = '' } = formData;

    useEffect(() => {
        const { cellData = [], cellCuttingData = [], observations = { cell: [], cellCutting: [] }, nonConformance = '' } = formData;

        const hasFormData =
            cellData.length > 0 ||
            cellCuttingData.length > 0 ||
            observations.cell.some(obs => obs.observation1 || obs.observation2) ||
            observations.cellCutting.some(obs => obs.observation1 || obs.observation2) ||
            nonConformance.trim() !== '';
    
        if (!hasFormData) {
            // If no formData, check metadata and inspectionData
            const metadata = JSON.parse(sessionStorage.getItem('metadata'));
            const inspectionData = JSON.parse(sessionStorage.getItem('inspectionData'));
    
            let prefilledCellData = [];
            let prefilledCellCuttingData = [];
            let prefilledObservations = { cell: [], cellCutting: [] };
            let prefilledNonConformance = '';
    
            if (metadata && metadata.pre && metadata.pre.CellAndCuttingMachine_pre) {
                prefilledCellData = metadata.pre.CellAndCuttingMachine_pre.cell || [];
                prefilledCellCuttingData = metadata.pre.CellAndCuttingMachine_pre.cellCutting || [];
                prefilledObservations = {
                    cell: prefilledCellData.map(() => ({ observation1: '', observation2: '' })),
                    cellCutting: prefilledCellCuttingData.map(() => ({ observation1: '', observation2: '' })),
                };
            }
    
            if (
                inspectionData &&
                inspectionData.PreLamination &&
                inspectionData.PreLamination.CellAndCuttingMachine_pre
            ) {
                const { cell, cellCutting, nonConformance: inspectionNonConformance } =
                    inspectionData.PreLamination.CellAndCuttingMachine_pre;
    
                prefilledCellData = cell || [];
                prefilledCellCuttingData = cellCutting || [];
                prefilledObservations = {
                    cell: cell.map(item => ({
                        observation1: item.observation1 || '',
                        observation2: item.observation2 || '',
                    })),
                    cellCutting: cellCutting.map(item => ({
                        observation1: item.observation1 || '',
                        observation2: item.observation2 || '',
                    })),
                };
                prefilledNonConformance = inspectionNonConformance || '';
            }
    
            if (
                prefilledCellData.length > 0 ||
                prefilledCellCuttingData.length > 0 ||
                prefilledObservations.cell.some(obs => obs.observation1 || obs.observation2) ||
                prefilledObservations.cellCutting.some(obs => obs.observation1 || obs.observation2) ||
                prefilledNonConformance.trim() !== ''
            ) {
                onFormDataChange({
                    ...formData,
                    cellData: prefilledCellData,
                    cellCuttingData: prefilledCellCuttingData,
                    observations: prefilledObservations,
                    nonConformance: prefilledNonConformance,
                });
            }
        }
    }, [formData, onFormDataChange]);

    const handleObservationChange = (table, index, field, value) => {
        const updatedObservations = { ...observations };
        updatedObservations[table][index][field] = value;
        onFormDataChange({ ...formData, observations: updatedObservations });
    };

    const handleNonConformanceChange = (value) => {
        onFormDataChange({ ...formData, nonConformance: value });
    };

    const submitObservations = async () => {
        const incompleteObservation =
            observations.cell.some((obs) => !obs.observation1.trim()) ||
            observations.cellCutting.some((obs) => !obs.observation1.trim());

        if (incompleteObservation) {
            alert('Please fill all Observation 1 fields before submitting.');
            return;
        }

        try {
            const projectId = localStorage.getItem('selectedProject');
            const inspectionDate = sessionStorage.getItem('inspectionDate');
            const inspectionShift = sessionStorage.getItem('inspectionShift');
            const inspectorName = sessionStorage.getItem('username');
            const inspectionSupplier = sessionStorage.getItem('inspectionSupplier');

            const formattedCellData = cellData.map((item, index) => ({
                product: item.product,
                specifications: item.specifications,
                frequency: item.frequency,
                observation1: observations.cell[index]?.observation1 || '',
                observation2: observations.cell[index]?.observation2 || '',
            }));

            const formattedCellCuttingData = cellCuttingData.map((item, index) => ({
                product: item.product,
                specifications: item.specifications,
                frequency: item.frequency,
                observation1: observations.cellCutting[index]?.observation1 || '',
                observation2: observations.cellCutting[index]?.observation2 || '',
            }));

            const payload = {
                projectId,
                inspectionSupplier,
                inspectionDate,
                inspectionShift,
                inspectorName,
                status: 'completed',
                PreLamination: {
                    CellAndCuttingMachine_pre: {
                        cell: formattedCellData,
                        cellCutting: formattedCellCuttingData,
                        nonConformance,
                    },
                },
            };

            await submitFormData(payload);

            alert('Observations submitted successfully!');
        } catch (error) {
            console.error('Error submitting observations:', error);
            alert('Failed to submit observations.');
        }
    };

    return (
        <div className="bg-white p-6 rounded-lg shadow-lg">
            <h3 className="text-xl font-semibold mb-4 text-gray-800">Cell & Cell Cutting Inspection</h3>

            {/* Cell Table */}
            <h4 className="text-lg font-semibold mt-6 mb-4 text-gray-700">Cell</h4>
            <div className="overflow-x-auto">
                <table className="min-w-full bg-white rounded-lg border-collapse table-auto">
                    <thead>
                        <tr className="bg-[#424752] text-white text-center">
                            <th className="py-3 px-4 min-w-[150px] rounded-tl-lg">Product</th>
                            <th className="py-3 px-4 min-w-[200px]">Specifications</th>
                            <th className="py-3 px-4 min-w-[150px]">Frequency</th>
                            <th className="py-3 px-4 min-w-[200px]">Observation 1</th>
                            <th className="py-3 px-4 min-w-[200px] rounded-tr-lg">Observation 2</th>
                        </tr>
                    </thead>
                    <tbody>
                        {cellData.map((item, index) => (
                            <tr
                                key={index}
                                className={`border-b last:border-none ${index % 2 === 0 ? 'bg-gray-50' : 'bg-white'} hover:bg-gray-100 transition-colors duration-200`}
                            >
                                <td className="py-3 px-4">{item.product}</td>
                                <td className="py-3 px-4">{item.specifications}</td>
                                <td className="py-3 px-4">{item.frequency}</td>
                                <td className="py-3 px-4">
                                    <input
                                        type="text"
                                        value={observations.cell[index]?.observation1 || ''}
                                        onChange={(e) => handleObservationChange('cell', index, 'observation1', e.target.value)}
                                        placeholder="Enter observation 1"
                                        className="w-full border border-gray-300 p-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 transition-all duration-150"
                                    />
                                </td>
                                <td className="py-3 px-4">
                                    <input
                                        type="text"
                                        value={observations.cell[index]?.observation2 || ''}
                                        onChange={(e) => handleObservationChange('cell', index, 'observation2', e.target.value)}
                                        placeholder="Enter observation 2"
                                        className="w-full border border-gray-300 p-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 transition-all duration-150"
                                    />
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Cell Cutting Table */}
            <h4 className="text-lg font-semibold mt-6 mb-4 text-gray-700">Cell Cutting</h4>
            <div className="overflow-x-auto">
                <table className="min-w-full bg-white rounded-lg border-collapse table-auto">
                    <thead>
                        <tr className="bg-[#424752] text-white text-center">
                            <th className="py-3 px-4 min-w-[150px] rounded-tl-lg">Product</th>
                            <th className="py-3 px-4 min-w-[200px]">Specifications</th>
                            <th className="py-3 px-4 min-w-[150px]">Frequency</th>
                            <th className="py-3 px-4 min-w-[200px]">Observation 1</th>
                            <th className="py-3 px-4 min-w-[200px] rounded-tr-lg">Observation 2</th>
                        </tr>
                    </thead>
                    <tbody>
                        {cellCuttingData.map((item, index) => (
                            <tr
                                key={index}
                                className={`border-b last:border-none ${index % 2 === 0 ? 'bg-gray-50' : 'bg-white'} hover:bg-gray-100 transition-colors duration-200`}
                            >
                                <td className="py-3 px-4">{item.product}</td>
                                <td className="py-3 px-4">{item.specifications}</td>
                                <td className="py-3 px-4">{item.frequency}</td>
                                <td className="py-3 px-4">
                                    <input
                                        type="text"
                                        value={observations.cellCutting[index]?.observation1 || ''}
                                        onChange={(e) => handleObservationChange('cellCutting', index, 'observation1', e.target.value)}
                                        placeholder="Enter observation 1"
                                        className="w-full border border-gray-300 p-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 transition-all duration-150"
                                    />
                                </td>
                                <td className="py-3 px-4">
                                    <input
                                        type="text"
                                        value={observations.cellCutting[index]?.observation2 || ''}
                                        onChange={(e) => handleObservationChange('cellCutting', index, 'observation2', e.target.value)}
                                        placeholder="Enter observation 2"
                                        className="w-full border border-gray-300 p-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 transition-all duration-150"
                                    />
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Non-Conformance */}
            <div className="mt-6">
                <h4 className="text-lg font-semibold mb-2 text-gray-700">Non Conformance</h4>
                <textarea
                    value={nonConformance}
                    onChange={(e) => handleNonConformanceChange(e.target.value)}
                    placeholder="Enter details about non conformance..."
                    className="w-full border border-gray-300 p-4 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 transition-all duration-150"
                    rows="4"
                />
            </div>

            {/* Submit Button */}
            <div className="mt-6">
                <button
                    onClick={submitObservations}
                    className="px-6 py-3 bg-[#424752] text-white font-semibold rounded-lg shadow-md hover:bg-gray-500 transition-colors duration-300"
                >
                    Submit
                </button>
            </div>
        </div>
    );
};

export default CellAndCuttingMachine;   